import { EyeOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Skeleton, Space, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import type { RootState } from "../../../store.ts"
import mergeObjects from "../../../utils/object/merge-objects.ts"
import { getProposalSyncStatus } from "../../DocumentSync/api/services.ts"
import { ProposalGenerateConfirmModal } from "../../DocumentSync/ProposalGenerateConfirmModal.tsx"
import type { FieldComparison } from "../../DocumentSync/types.ts"
import { contractValidationHasIssues } from "../ContractValidation/calculate-contract-validation.ts"
import { showDocumentValidationError } from "../helpers/show-document-validation-error.tsx"
import { getProposalValidation } from "../ProposalValidation/api/services.ts"
import { ProposalValidationCard } from "../ProposalValidation/ProposalValidationCard.tsx"
import { ProposalValidationConfirmModal } from "../ProposalValidation/ProposalValidationConfirmModal.tsx"
import type { ProposalValidationResult } from "../ProposalValidation/types.ts"
import {
  createProposalDocument,
  saveProposalDocument,
  sendProposalDocumentToApproval,
  updateProposalDocument
} from "../redux/thunks/document-proposal.thunks.ts"
import {
  fetchDocumentInitialData,
  fetchDocumentPreview,
  fetchTemplateById
} from "../redux/thunks/document-templates.thunks.ts"
import { type DocumentRequestData } from "../types/document.types.ts"
import { type TemplateDocumentFormData } from "../types/template-document-form-data.tsx"
import type { DocumentInitialData, DocumentTemplate } from "../types/types.ts"
import { DocumentBackToLeadBtn } from "./components/DocumentBackToLeadBtn.tsx"
import { TemplateDocumentForm } from "./components/TemplateDocumentForm/TemplateDocumentForm.tsx"
import { TemplateDocumentPreview } from "./components/TemplateDocumentPreview.tsx"

const { Title, Text } = Typography

function DocumentProposalCreatePage({
  getInitialData,
  getTemplate,
  createDocument,
  updateDocument,
  saveDocument,
  sendDocumentToApproval,
  fetchDocumentPreview
}: ConnectedProps<typeof connector>) {
  const [form] = Form.useForm<TemplateDocumentFormData>()

  const { normalizeDate } = useDateFormat()
  const navigate = useNavigate()
  const params = useParams<{ templateId: string; leadId: string }>()

  const templateId = params?.templateId as string
  const leadId = params?.leadId as string

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [template, setTemplate] = useState<DocumentTemplate | null>(null)
  const [initialData, setInitialData] = useState<DocumentInitialData | null>(
    null
  )
  // Caches the just-created document's id once the first save succeeds, so a
  // second click (draft, generate, or a retry after cancelling the sync
  // modal below) never re-POSTs and creates a duplicate document — every
  // finishing action after the first funnels through this same record.
  const [created, setCreated] = useState<{
    id: string
    leadId: string
  } | null>(null)
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false)
  const [generateConfirmDiffs, setGenerateConfirmDiffs] = useState<
    FieldComparison[]
  >([])
  const [generateConfirmDocumentLabel, setGenerateConfirmDocumentLabel] =
    useState<string | undefined>(undefined)
  const [pendingFinish, setPendingFinish] = useState<{
    documentId: string
    leadId: string
    kind: "generate" | "approval"
  } | null>(null)
  const [validationConfirmOpen, setValidationConfirmOpen] = useState(false)
  const [validationResult, setValidationResult] =
    useState<ProposalValidationResult | null>(null)
  // The `{id, leadId}` a confirmed "Generate Anyway" resumes with — only
  // ever set right before opening the validation modal, so a stale value
  // can never resume a check that's still in progress. Mirrors
  // `DocumentContractCreatePage`'s own `pendingValidationConfirm`.
  const [pendingValidationConfirm, setPendingValidationConfirm] = useState<{
    id: string
    leadId: string
  } | null>(null)

  const handleFetchDocumentPreview = useCallback(
    async (data: DocumentRequestData) => {
      return fetchDocumentPreview(data).unwrap()
    },
    [fetchDocumentPreview]
  )

  const handleCreateDocument = useCallback(async () => {
    try {
      const values = await form.validateFields()

      return createDocument({
        lead_id: initialData?.lead_id,
        customer_id: initialData?.customer_id,
        template_id: initialData?.template_id,
        ...values,
        completion_date: normalizeDate(values?.completion_date),
        start_date: normalizeDate(values?.start_date)
      }).unwrap()
    } catch (e) {
      showDocumentValidationError(form, e)
      console.log(e)
    }
  }, [
    createDocument,
    form,
    initialData?.blocks,
    initialData?.customer_id,
    initialData?.lead_id,
    initialData?.template_id
  ])

  // Same shape as `DocumentProposalEditPage`'s own `handleUpdateDocument` —
  // PATCHes the form's current values to an already-created document. Errors
  // (validation included) are handled here, not rethrown, matching
  // `handleCreateDocument`'s own swallow-and-return-undefined convention so
  // `ensureCreated` can treat "create failed validation" and "re-save failed
  // validation" the same way.
  const handleUpdateDocument = useCallback(
    async (id: string) => {
      try {
        const values = await form.validateFields()
        return await updateDocument({
          id,
          data: {
            ...values,
            completion_date: normalizeDate(values?.completion_date),
            start_date: normalizeDate(values?.start_date)
          }
        })?.unwrap()
      } catch (e) {
        showDocumentValidationError(form, e)
        console.log(e)
        return undefined
      }
    },
    [form, normalizeDate, updateDocument]
  )

  // Creates the document once, then PATCHes the current form values on every
  // subsequent call rather than short-circuiting on the cached id alone.
  // Without the PATCH, a field edited after the first Save (e.g. the user
  // closes the sync-confirm modal without confirming, edits a field, and
  // clicks Save & Generate again) was silently discarded: `saveDocument`/
  // `finishGenerate` below carries only a document id, no field data, so a
  // "no new diff" second run skipped straight to generating a PDF from the
  // stale, first-created values. Mirrors `DocumentProposalEditPage.
  // handleUpdateDocument`, which every save already runs before its own
  // sync-check — there, every save already had an existing document to
  // update; here, only the second and later saves do.
  const ensureCreated = useCallback(async () => {
    if (created) {
      const updated = await handleUpdateDocument(created.id)
      if (!updated) return null
      return created
    }

    const createdDocument = await handleCreateDocument()
    const id = createdDocument?.id
    const docLeadId = createdDocument?.lead_id
    if (!id || !docLeadId) return null

    const result = { id, leadId: docLeadId }
    setCreated(result)
    return result
  }, [created, handleCreateDocument, handleUpdateDocument])

  // save draft without pdf
  const handleSaveDraft = useCallback(async () => {
    try {
      setSaving(true)
      const result = await ensureCreated()
      if (!result) return

      navigate(RoutesPaths.lead.view(result.leadId))
    } catch (e) {
      console.log(e)
    } finally {
      setSaving(false)
    }
  }, [ensureCreated, navigate])

  // The actual PDF-render/save-file step — shared by the no-diff path
  // (called immediately) and the `ProposalGenerateConfirmModal` resolution,
  // whichever way it went, after its own sync call (if any) resolves.
  const finishGenerate = useCallback(
    async (documentId: string, docLeadId: string) => {
      try {
        setSaving(true)
        await saveDocument(documentId)
        navigate(RoutesPaths.lead.view(docLeadId, { tab: "documents" }))
      } catch (e) {
        console.log(e)
      } finally {
        setSaving(false)
      }
    },
    [navigate, saveDocument]
  )

  const finishSendToApproval = useCallback(
    async (documentId: string, docLeadId: string) => {
      try {
        setSaving(true)
        await sendDocumentToApproval(documentId).unwrap()
        navigate(RoutesPaths.lead.view(docLeadId))
      } catch (e) {
        console.log(e)
      } finally {
        setSaving(false)
      }
    },
    [navigate, sendDocumentToApproval]
  )

  // The gap this closes: "Save & Generate"/"Send to Approval" on a
  // brand-new Proposal used to create-and-navigate with no comparison
  // against the Lead at all — `ProposalGenerateConfirmModal` only ever
  // appeared later, on a *subsequent* edit-and-regenerate, because only
  // `DocumentProposalEditPage` ran this check. The document now has to
  // exist (a real id) before `getProposalSyncStatus` can compare it against
  // anything, so create-once via `ensureCreated`, then run the exact same
  // check the edit page runs, before either finishing action. Extracted
  // from `runWithSyncCheck` so the Proposal Validation gate below can
  // resume at this exact point after "Generate Anyway", without
  // duplicating it — mirrors `DocumentContractCreatePage.
  // proceedWithSyncCheck`.
  const proceedWithSyncCheck = useCallback(
    async (
      result: { id: string; leadId: string },
      kind: "generate" | "approval"
    ) => {
      const statusRes = await getProposalSyncStatus(result.id)
      const diffs = statusRes.ok
        ? (statusRes.data?.fields ?? []).filter(
            (field) => field.has_diff && field.key !== "sales_owner"
          )
        : []

      if (!diffs.length) {
        if (kind === "generate") {
          await finishGenerate(result.id, result.leadId)
        } else {
          await finishSendToApproval(result.id, result.leadId)
        }
        return
      }

      setGenerateConfirmDiffs(diffs)
      setGenerateConfirmDocumentLabel(
        statusRes.ok ? statusRes.data?.document_label : undefined
      )
      setPendingFinish({ documentId: result.id, leadId: result.leadId, kind })
      setGenerateConfirmOpen(true)
    },
    [finishGenerate, finishSendToApproval]
  )

  const runWithSyncCheck = useCallback(
    async (kind: "generate" | "approval") => {
      try {
        setSaving(true)
        const result = await ensureCreated()
        if (!result) return

        // The validation gate only applies to "Generate Proposal" itself,
        // per spec — "Send to Approval" is unaffected. Same
        // never-block-on-failure swallow as the Edit page: an
        // unreachable/erroring check must not stop Generate.
        if (kind === "generate") {
          let validation: ProposalValidationResult | null = null
          try {
            const validationRes = await getProposalValidation(result.id)
            validation = validationRes.ok ? (validationRes.data ?? null) : null
          } catch {
            validation = null
          }

          if (validation && contractValidationHasIssues(validation)) {
            setValidationResult(validation)
            setPendingValidationConfirm(result)
            setValidationConfirmOpen(true)
            return
          }
        }

        await proceedWithSyncCheck(result, kind)
      } catch (e) {
        console.log(e)
      } finally {
        setSaving(false)
      }
    },
    [ensureCreated, proceedWithSyncCheck]
  )

  const handleValidationConfirmProceed = useCallback(async () => {
    if (!pendingValidationConfirm) return
    try {
      setSaving(true)
      await proceedWithSyncCheck(pendingValidationConfirm, "generate")
    } catch (e) {
      console.log(e)
    } finally {
      setSaving(false)
    }
  }, [pendingValidationConfirm, proceedWithSyncCheck])

  // save pdf
  const handleSaveDocument = useCallback(
    () => runWithSyncCheck("generate"),
    [runWithSyncCheck]
  )

  const handleSendToApproval = useCallback(
    () => runWithSyncCheck("approval"),
    [runWithSyncCheck]
  )

  const handleGenerateConfirmProceed = useCallback(async () => {
    if (!pendingFinish) return

    if (pendingFinish.kind === "generate") {
      await finishGenerate(pendingFinish.documentId, pendingFinish.leadId)
    } else {
      await finishSendToApproval(pendingFinish.documentId, pendingFinish.leadId)
    }
  }, [pendingFinish, finishGenerate, finishSendToApproval])

  const handleGetAllData = useCallback(async () => {
    if (!leadId || !templateId) {
      return console.error(
        "Lead id and template id are required to fetch initial data"
      )
    }

    try {
      const initDataRes = await getInitialData({ templateId, leadId }).unwrap()
      if (initDataRes) setInitialData(initDataRes)

      const templateRes = await getTemplate(templateId).unwrap()
      if (templateRes) setTemplate(templateRes)
    } finally {
      setLoading(false)
    }
  }, [getInitialData, getTemplate, leadId, templateId])

  const handleSubmit = useCallback(
    async (data: TemplateDocumentFormData) => {
      try {
        setSaving(true)
        await handleSaveDocument()
      } catch (e) {
        console.log()
      } finally {
        setSaving(false)
      }
    },
    [handleSaveDocument]
  )

  const mergedInitialValues = useMemo(() => {
    if (!initialData) return undefined
    if (!template) return initialData

    const mergedData = mergeObjects(initialData, template, {
      base: "first",
      mergeExtra: true
    })

    return { ...(mergedData || {}), phases: initialData?.phases || [] }
  }, [initialData, template])

  useEffect(() => {
    void handleGetAllData()
    generateDocumentTitle("Create Document")
  }, [handleGetAllData])

  if (!leadId) return <div>Lead id is required</div>
  if (loading) return <Skeleton.Node active className={"size-full"} />
  if (!template) return <div>Template not found</div>

  return (
    <FixedContainer>
      <ProposalValidationConfirmModal
        open={validationConfirmOpen}
        onClose={() => setValidationConfirmOpen(false)}
        result={validationResult}
        onProceed={handleValidationConfirmProceed}
      />
      <ProposalGenerateConfirmModal
        open={generateConfirmOpen}
        onClose={() => setGenerateConfirmOpen(false)}
        diffs={generateConfirmDiffs}
        proposalId={pendingFinish?.documentId ?? ""}
        documentLabel={generateConfirmDocumentLabel}
        onProceed={handleGenerateConfirmProceed}
      />
      <Flex vertical gap={"small"} className={"overflow-hidden"}>
        <Flex justify={"space-between"} align={"center"} wrap gap={"small"}>
          <Flex vertical>
            <Title level={4}>Create Document</Title>
            <Text type="secondary">Template: {template?.name}</Text>
          </Flex>
          <Space wrap>
            {leadId && (
              <DocumentBackToLeadBtn leadId={leadId} saving={saving} />
            )}
            <TemplateDocumentPreview
              saving={saving}
              form={form}
              templateId={initialData?.template_id}
              leadId={initialData?.lead_id}
              customerId={initialData?.customer_id}
              fetchDocumentPreview={handleFetchDocumentPreview}
              submitLabel={"Save as Draft"}
              onSubmitClick={handleSaveDraft}
              onGenerateClick={handleSaveDocument}
              onClickSendToApproval={handleSendToApproval}
            />

            <Button
              loading={saving}
              onClick={form.submit}
              variant={"solid"}
              color={"primary"}
              icon={<EyeOutlined />}
            >
              Save & Generate
            </Button>
          </Space>
        </Flex>

        <ProposalValidationCard form={form} className={"mb-3"} />

        <TemplateDocumentForm
          form={form}
          saving={saving}
          initialValues={mergedInitialValues}
          projectTypes={initialData?.project_type_details || []}
          leadId={leadId}
          // initialValues={{ ...(initialData || {}), ...FULL_MOCK } as any}
          onSubmit={handleSubmit}
        />
      </Flex>
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({})

const mapDispatchToProps = {
  // getBlocks: fetchAvailableBlocks,
  getInitialData: fetchDocumentInitialData,
  getTemplate: fetchTemplateById,
  createDocument: createProposalDocument,
  updateDocument: updateProposalDocument,
  saveDocument: saveProposalDocument,
  sendDocumentToApproval: sendProposalDocumentToApproval,
  fetchDocumentPreview
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(DocumentProposalCreatePage)
